import {
  CheckCircle2, XCircle, Trash2, Coins, AlertTriangle, ShieldAlert, Heart,
} from "lucide-react";

function statusPillClasses(status, isFlagged) {
  if (status === "approved") return "border-[#16A34A] bg-[#16A34A] text-white";
  if (status === "rejected") return "border-[#DC2626] bg-[#FEE2E2] text-[#DC2626]";
  if (isFlagged) return "border-[#09090B] bg-[#09090B] text-[#FFCC00]";
  return "border-[#09090B] bg-[#FFCC00] text-[#09090B]";
}

function isSubmissionFlagged(it) {
  if (it.status === "flagged") return true;
  return !!(it.moderation && it.moderation.safe === false);
}

function SelectionCell({ checkable, isChecked, onToggle, id }) {
  return (
    <div className="flex items-start justify-center pt-5 bg-[#F4F4F5] border-r-2 border-[#09090B]">
      {checkable ? (
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onToggle}
          data-testid={`select-${id}`}
          className="w-4 h-4 accent-[#FF5A00]"
        />
      ) : (
        <CheckCircle2 strokeWidth={2.5} className="w-4 h-4 text-[#16A34A]" />
      )}
    </div>
  );
}

function PhotoCell({ photoUrl, alt }) {
  return (
    <div className="aspect-square md:aspect-auto bg-[#F4F4F5] border-r-2 border-[#09090B] overflow-hidden">
      <img
        src={photoUrl}
        alt={alt}
        className="w-full h-full object-cover"
      />
    </div>
  );
}

function ModeratorNote({ reason }) {
  return (
    <div className="mt-3 border-2 border-[#FFCC00] bg-[#FFFBEA] p-3 text-sm flex items-start gap-2">
      <AlertTriangle strokeWidth={2.5} className="w-4 h-4 text-[#FF5A00] mt-0.5 shrink-0" />
      <span className="text-[#09090B]">
        <strong>Moderator note:</strong> {reason}
      </span>
    </div>
  );
}

function SubmissionMeta({ submission, isFlagged, isRedeemed }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className={`ws-label px-3 py-1 border-2 ${statusPillClasses(submission.status, isFlagged)}`}>
        {submission.status}
      </span>
      {isFlagged && (
        <span className="ws-label inline-flex items-center gap-1 text-[#09090B]">
          <ShieldAlert strokeWidth={2.5} className="w-4 h-4 text-[#FF5A00]" />
          AI-flagged · {submission.moderation?.category || "review"}
        </span>
      )}
      {isRedeemed && (
        <span className="ws-label inline-flex items-center gap-1 bg-[#09090B] text-white px-2 py-0.5">
          <Coins strokeWidth={2.5} className="w-3.5 h-3.5" /> Redeemed
        </span>
      )}
      <span className="font-display font-bold text-lg tracking-tight">{submission.employee_name}</span>
      <span className="text-sm text-[#52525B]">ID · {submission.employee_id}</span>
      <span className="text-xs text-[#52525B]">{new Date(submission.created_at).toLocaleString()}</span>
    </div>
  );
}

function ActionsCell({ submission, busy, onApprove, onReject, onDelete }) {
  const disabled = busy === submission.id;
  return (
    <div className="p-5 border-t-2 md:border-t-0 md:border-l-2 border-[#09090B] flex md:flex-col gap-2 justify-end items-stretch min-w-[180px] bg-[#F4F4F5]">
      {submission.status !== "approved" && (
        <button
          type="button"
          onClick={() => onApprove(submission.id)}
          disabled={disabled}
          data-testid={`approve-${submission.id}`}
          className="inline-flex items-center justify-center gap-2 bg-[#16A34A] text-white ws-label px-4 py-2 border-2 border-[#09090B] hover:bg-[#15803D] transition-colors disabled:opacity-60"
        >
          <CheckCircle2 strokeWidth={2.5} className="w-4 h-4" /> Approve
        </button>
      )}
      {submission.status !== "rejected" && (
        <button
          type="button"
          onClick={() => onReject(submission.id)}
          disabled={disabled}
          data-testid={`reject-${submission.id}`}
          className="inline-flex items-center justify-center gap-2 bg-white text-[#09090B] ws-label px-4 py-2 border-2 border-[#09090B] hover:bg-[#FEE2E2] hover:text-[#DC2626] transition-colors disabled:opacity-60"
        >
          <XCircle strokeWidth={2.5} className="w-4 h-4" /> Reject
        </button>
      )}
      <button
        type="button"
        onClick={() => onDelete(submission.id)}
        disabled={disabled}
        data-testid={`delete-${submission.id}`}
        className="inline-flex items-center justify-center gap-2 bg-[#09090B] text-white ws-label px-4 py-2 border-2 border-[#09090B] hover:bg-[#DC2626] transition-colors disabled:opacity-60"
      >
        <Trash2 strokeWidth={2.5} className="w-4 h-4" /> Delete
      </button>
    </div>
  );
}

export default function SubmissionRow({ submission, selected, busy, onToggleSelect, onApprove, onReject, onDelete }) {
  const isFlagged = isSubmissionFlagged(submission);
  const isRedeemed = !!submission.redeemed_at;
  const checkable = submission.status !== "approved";
  const isChecked = selected.has(submission.id);

  return (
    <article
      key={submission.id}
      className={`border-2 border-[#09090B] bg-white grid md:grid-cols-[40px_200px_1fr_auto] gap-0 ws-hard-shadow-sm overflow-hidden ${
        isFlagged ? "ring-4 ring-[#FFCC00]" : ""
      }`}
      data-testid={`submission-${submission.id}`}
    >
      <SelectionCell
        checkable={checkable}
        isChecked={isChecked}
        onToggle={() => onToggleSelect(submission.id)}
        id={submission.id}
      />
      <PhotoCell photoUrl={submission.photo_url} alt={submission.employee_name} />
      <div className="p-5">
        <SubmissionMeta submission={submission} isFlagged={isFlagged} isRedeemed={isRedeemed} />
        <p className="mt-3 text-[#09090B] leading-relaxed">{submission.reason}</p>
        {isFlagged && submission.moderation?.reason && (
          <ModeratorNote reason={submission.moderation.reason} />
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 bg-[#F4F4F5] border-2 border-[#09090B] px-3 py-1">
            <span className="ws-label text-[#52525B]">Token</span>
            <span className="font-mono font-bold text-[#09090B]">{submission.token_code}</span>
          </div>
          {submission.applause > 0 && (
            <div
              className="inline-flex items-center gap-2 bg-[#FFCC00] border-2 border-[#09090B] px-3 py-1"
              data-testid={`applause-stat-${submission.id}`}
            >
              <Heart strokeWidth={2.5} className="w-4 h-4 fill-[#09090B]" />
              <span className="font-mono font-bold text-[#09090B]">{submission.applause}</span>
              <span className="ws-label text-[#09090B]">applause</span>
            </div>
          )}
        </div>
      </div>
      <ActionsCell
        submission={submission}
        busy={busy}
        onApprove={onApprove}
        onReject={onReject}
        onDelete={onDelete}
      />
    </article>
  );
}
